if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        const pageParams = new URLSearchParams(window.location.search);
        window.pageNumber = decodeURIComponent(pageParams.get('page') || '');
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      getInput() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.getInput().addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.getInput().addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.getInput().blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.closest('.collection').dataset.id}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(
                `#quick-add-bulk-${this.dataset.id}-${this.closest('.collection').dataset.id}`
              );
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        
        // For each item, we need to add it individually with properties
        const promises = [];
        
        Object.entries(items).forEach(([variantId, quantity]) => {
          if (quantity > 0) {
            // Add item with properties
            promises.push(this.addItemWithProperties(variantId, quantity));
          } else {
            // Remove item (quantity = 0) - no properties needed
            promises.push(this.removeItem(variantId));
          }
        });

        Promise.all(promises)
          .then((responses) => {
            // Use the last response to update sections
            const lastResponse = responses[responses.length - 1];
            if (lastResponse) {
              this.renderSections(lastResponse, ids);
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: lastResponse });
            }
          })
          .catch((error) => {
            console.error('Quick Add Bulk - Error updating quantities:', error);
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.requestStarted = false;
          });
      }

      addItemWithProperties(variantId, quantity) {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
        
        // Add properties
        const properties = this.collectLineItemProperties();
        Object.entries(properties).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });

        // Add sections for rendering
        formData.append('sections', this.getSectionsToRender().map((section) => section.section));
        formData.append('sections_url', this.getSectionsUrl());

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        console.log('Quick Add Bulk - Adding item with properties:', {
          variantId: variantId,
          quantity: quantity,
          properties: properties
        });

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            console.log('Quick Add Bulk - Cart add response:', response);
            if (response.status) {
              throw new Error(response.description || 'Failed to add item');
            }
            return response;
          })
          .catch((error) => {
            console.error('Quick Add Bulk - Error adding item:', error);
            throw error;
          });
      }

      removeItem(variantId) {
        const body = JSON.stringify({
          updates: { [variantId]: 0 },
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        console.log('Quick Add Bulk - Removing item:', {
          variantId: variantId,
          body: body
        });

        return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            console.log('Quick Add Bulk - Remove item response:', parsedState);
            return parsedState;
          })
          .catch((error) => {
            console.error('Quick Add Bulk - Error removing item:', error);
            throw error;
          });
      }

      collectLineItemProperties() {
        // Get the product ID from the quick add bulk element
        const productId = this.dataset.id;
        
        // Try to get the lead time from a data attribute or find it in the DOM
        let leadTime = "N/A"; // Default value
        let hasDropshipTag = false;
        
        // Look for lead time in data attributes or hidden inputs
        const leadTimeElement = this.querySelector('[data-lead-time]');
        if (leadTimeElement) {
          leadTime = leadTimeElement.dataset.leadTime;
          hasDropshipTag = leadTimeElement.dataset.hasDropshipTag === 'true';
        }
        
        console.log('Quick Add Bulk - Lead Time Property Check:', {
          productId: productId,
          leadTime: leadTime,
          hasDropshipTag: hasDropshipTag,
          elementFound: !!leadTimeElement,
          shouldAddProperty: hasDropshipTag
        });
        
        // Add the property if product has 'dropship' in stock_status
        if (hasDropshipTag) {
          // Use the actual lead time if available, otherwise use a default message
          const leadTimeValue = leadTime !== 'N/A' ? leadTime : 'This item may be shipped separately';
          return {
            "Lead Time": leadTimeValue
          };
        } else {
          return {}; // Return empty object if not a dropship product
        }
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.id}-${this.closest('.collection-quick-add-bulk').dataset.id}`,
            section: this.closest('.collection-quick-add-bulk').dataset.id,
            selector: `#quick-add-bulk-${this.dataset.id}-${this.closest('.collection-quick-add-bulk').dataset.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '#CartDrawer',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (
            sectionElement &&
            sectionElement.parentElement &&
            sectionElement.parentElement.classList.contains('drawer')
          ) {
            parsedState.items.length > 0
              ? sectionElement.parentElement.classList.remove('is-empty')
              : sectionElement.parentElement.classList.add('is-empty');

            setTimeout(() => {
              document.querySelector('#CartDrawer-Overlay').addEventListener('click', this.cart.close.bind(this.cart));
            });
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
